import {useEffect, useRef} from "react";

import {usePathname, useRouter, useSearchParams} from "next/navigation";

import type {INavigable} from "@/shared/contracts";

interface UseRouterBridgeOptions {
    /**
     * Skip the initial URL sync on mount
     * Useful when model is already initialized with URL params
     */
    skipInitialSync?: boolean;
    /**
     * Sync only once on mount, then ignore all URL changes
     * Useful when you want to initialize from URL but prevent further syncs
     */
    syncOnce?: boolean;
}

export function useRouterBridge(model: INavigable, options: UseRouterBridgeOptions = {}): void {
    const router = useRouter();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const isFirstRender = useRef(true);
    const hasSynced = useRef(false);

    // Synchronization: model → router
    // Only for refresh (navigate/replace now through window.history API)
    useEffect(() => {
        const unsubBack = model.routerBridge.events.on("back", () => {
            router.back();
        });

        const unsubForward = model.routerBridge.events.on("forward", () => {
            router.forward();
        });

        const unsubRefresh = model.routerBridge.events.on("refresh", () => {
            router.refresh();
        });

        return () => {
            unsubBack();
            unsubForward();
            unsubRefresh();
        };
    }, [router, model]);

    // Synchronization: router → model (tracking URL changes)
    useEffect(() => {
        // If syncOnce is enabled and we already synced, skip
        if (options.syncOnce && hasSynced.current) {
            return;
        }

        // Skip the initial sync if option is enabled
        if (options.skipInitialSync && isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // Skip if this is our own navigation (internal update)
        const currentQueryString = searchParams?.toString() ?? "";
        if (model.routerBridge.pendingQueryString === currentQueryString) {
            model.routerBridge.pendingQueryString = null;
            isFirstRender.current = false;
            return;
        }
        model.routerBridge.pendingQueryString = null;

        if (searchParams && pathname && model.onUrlChange) {
            model.onUrlChange(pathname, searchParams);
        }

        // Mark as synced if syncOnce is enabled
        if (options.syncOnce) {
            hasSynced.current = true;
        }

        if (isFirstRender.current) {
            isFirstRender.current = false;
        }
    }, [searchParams, pathname, model, options.skipInitialSync, options.syncOnce]);
}
